/**
 * The colour worlds' line icons on the web — the same 24-unit drawings as the
 * app (mobile/components/colour-worlds/icons.tsx) and the design board.
 */

type Shape = ["path", { d: string }] | ["circle", { cx: number; cy: number; r: number }] | ["rect", { x: number; y: number; width: number; height: number; rx?: number }];

const ICONS: Record<string, Shape[]> = {
  search: [["circle", { cx: 11, cy: 11, r: 7 }], ["path", { d: "m20 20-3.5-3.5" }]],
  bag: [["path", { d: "M4.5 8h15l-1 12.2a1 1 0 0 1-1 .8H6.5a1 1 0 0 1-1-.8L4.5 8Z" }], ["path", { d: "M9 8V6.5a3 3 0 0 1 6 0V8" }]],
  arrow: [["path", { d: "M5 12h14" }], ["path", { d: "m13 6 6 6-6 6" }]],
  pin: [["path", { d: "M12 21s-6.5-6.2-6.5-11.2a6.5 6.5 0 0 1 13 0C18.5 14.8 12 21 12 21Z" }], ["circle", { cx: 12, cy: 9.8, r: 2.4 }]],
  chat: [["path", { d: "M20 11.6a8 8 0 0 1-11.7 7.1L4 20l1.3-4.1A8 8 0 1 1 20 11.6Z" }]],
  wheel: [["circle", { cx: 12, cy: 12, r: 8.5 }], ["path", { d: "M12 3.5v17" }], ["path", { d: "M3.5 12h17" }], ["path", { d: "m6 6 12 12" }], ["path", { d: "m18 6-12 12" }]],
  trivia: [["circle", { cx: 12, cy: 12, r: 8.5 }], ["path", { d: "M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.1-2.4 3.7" }], ["path", { d: "M12 16.8v.2" }]],
  gift: [
    ["rect", { x: 4, y: 9, width: 16, height: 11, rx: 1.5 }],
    ["path", { d: "M3 9h18" }],
    ["path", { d: "M12 9v11" }],
    ["path", { d: "M12 9c-1.5-3.5-5.5-4-5.5-1.5S9.5 9 12 9Zm0 0c1.5-3.5 5.5-4 5.5-1.5S14.5 9 12 9Z" }],
  ],
  grabber: [["path", { d: "M12 3v6" }], ["path", { d: "M7 9h10" }], ["path", { d: "M7 9v3.5L9.5 15" }], ["path", { d: "M17 9v3.5L14.5 15" }], ["circle", { cx: 12, cy: 18.5, r: 2.5 }]],
  stack: [["path", { d: "m12 4 8 4-8 4-8-4 8-4Z" }], ["path", { d: "m4 12 8 4 8-4" }], ["path", { d: "m4 16 8 4 8-4" }]],
  drop: [["path", { d: "M12 4v11" }], ["path", { d: "m7 10 5 5 5-5" }], ["path", { d: "M5 20h14" }]],
  match: [["rect", { x: 4, y: 4, width: 7, height: 7, rx: 1.5 }], ["rect", { x: 13, y: 13, width: 7, height: 7, rx: 1.5 }], ["path", { d: "M13 7.5h4.5V11" }]],
};

/** Game type → its icon (same map as the app). */
export const GAME_ICON: Record<string, string> = {
  spin: "wheel",
  trivia: "trivia",
  giftbox: "gift",
  grabber: "grabber",
  stacker: "stack",
  drop: "drop",
  match: "match",
};

export function Icon({ name, size = 20, strokeWidth = 1.8, className }: { name: string; size?: number; strokeWidth?: number; className?: string }) {
  const shapes = ICONS[name] ?? ICONS.arrow;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {shapes.map(([kind, p], i) => (kind === "path" ? <path key={i} {...p} /> : kind === "circle" ? <circle key={i} {...p} /> : <rect key={i} {...p} />))}
    </svg>
  );
}
